import type { FetchBaseQueryError } from '@reduxjs/toolkit/query/react';
import { runInChunks } from '@/lib/fetchAllPages';
import { vendorApi } from './_base';
import { vendorProductsUrl } from './vendorProducts';
import type { VendorProductRow, VendorProductsResponse } from './vendorProducts';
import { merchantVendorProductsUrl } from './merchantVendorProducts';
import type { MerchantVendorProductsResponse } from './merchantVendorProducts';

/**
 * Multi-vendor catalog sample + search (`getVendorProductsForVendors`) and the
 * per-vendor overrides probe (`getMerchantLinkedVendors`) — the routing
 * simulator's SKU source. Split out of `vendorProducts.ts` to keep that file
 * under the 400-LOC budget (see `./index.ts`).
 */

/** Rows fetched per vendor per request. Deliberately a SAMPLE, never the full
 * catalog: vendor catalogs run 30K–60K+ products (see `fetchAllPages`' own
 * docs), so "download everything" fires hundreds of paged requests per vendor
 * and the picker never settles. Narrowing happens server-side via
 * `searchString` instead. */
export const VENDOR_PRODUCTS_SAMPLE_PAGE_SIZE = 100;

/** Args for the per-vendor catalog sample. */
export interface VendorProductsForVendorsArgs {
  vendorIds: string[];
  /** Server-side search term, forwarded per vendor as `searchAll`. */
  searchString?: string;
  /** Required for the linked tier: vendors in `linkedVendorIds` are read from
   * the merchant-override catalog, which is keyed by merchant. */
  merchantId?: string;
  /** Vendors with merchant-linked overrides (from `getMerchantLinkedVendors`).
   * Their rows come from `/v1/merchantVendorProducts` — the set the routing
   * engine actually evaluates for them — instead of the full default catalog.
   * Omit for callers that cannot read the override layer (merchant users). */
  linkedVendorIds?: string[];
}

/** One sample row — the live `GET /v1/vendorProducts` response carries the
 * owning vendor inline (vendorId + vendorName) beyond what `VendorProductRow`
 * declares. Captured from QA with a merchant-user token. */
export interface VendorProductWithVendorRow extends VendorProductRow {
  vendorId?: string;
  vendorName?: string;
  /** True when the row came from the merchant-linked override catalog. */
  isLinked?: boolean;
}

export interface VendorProductsForVendorsResponse {
  vendorProducts: VendorProductWithVendorRow[];
  /** Sum of the vendors' matching-row totals. When it exceeds the returned
   * row count the sample is truncated — the caller should prompt the user to
   * narrow by typing rather than pretend the list is complete. */
  total: number;
  /** Vendors whose page request failed while others loaded. Non-empty means
   * the catalog is PARTIAL — the caller must say so instead of presenting the
   * survivors as the whole picture. (Every vendor failing is a query error.) */
  failedVendorIds: string[];
}

/** Args for the overrides probe. */
export interface MerchantLinkedVendorsArgs {
  merchantId: string;
  vendorIds: string[];
}

/**
 * Interleave per-vendor buckets round-robin (A1, B1, C1, A2, B2, …) so the
 * picker's initial list alternates vendors instead of showing all of vendor
 * A's sample before vendor B's. Ported from the legacy simulator picker.
 */
export function interleaveRoundRobin<T>(buckets: T[][]): T[] {
  const out: T[] = [];
  for (let i = 0; ; i += 1) {
    let added = false;
    for (const bucket of buckets) {
      if (i < bucket.length) {
        out.push(bucket[i] as T);
        added = true;
      }
    }
    if (!added) return out;
  }
}

const vendorProductsForVendorsApi = vendorApi.injectEndpoints({
  endpoints: (builder) => ({
    /**
     * Which of the merchant's vendors have linked (override) products —
     * `GET /v1/merchantVendorProducts?merchantId=&vendorId=&page=1&pageSize=1`
     * per vendor, keeping only vendors with `total > 0`.
     *
     * The routing engine evaluates the LINKED set for a vendor that has any
     * override and the full default catalog otherwise, so this probe is what
     * lets the SKU sample reproduce that merge per vendor. Cached separately
     * from the sample (no search term in the key) so typing never re-probes.
     *
     * A vendor whose probe 403s is treated as having NO overrides rather
     * than failing the picker: the override layer 403s for tokens that can't
     * read it (a known service gap), and the full-catalog fallback is the
     * correct degraded behavior for exactly that caller. Any OTHER failure
     * (500, timeout) is a real error and propagates — silently mapping it to
     * "no overrides" would show a full default catalog for a vendor the
     * engine routes from its linked set, and cache that wrong answer.
     */
    getMerchantLinkedVendors: builder.query<string[], MerchantLinkedVendorsArgs>({
      async queryFn({ merchantId, vendorIds }, _api, _extra, fetchWithBQ) {
        let firstError: FetchBaseQueryError | undefined;
        const flags = await runInChunks(vendorIds, async (vendorId) => {
          const res = await fetchWithBQ({
            url: merchantVendorProductsUrl({ merchantId, vendorId, page: 1, pageSize: 1 }),
            method: 'GET',
          });
          if (res.error) {
            if (res.error.status !== 403) firstError ??= res.error;
            return false;
          }
          return ((res.data as MerchantVendorProductsResponse).total ?? 0) > 0;
        });
        if (firstError) return { error: firstError };
        return { data: vendorIds.filter((_, i) => flags[i]) };
      },
      // The probed answer changes exactly when the merchant's overrides for a
      // vendor change — the same per-vendor `mvp:` tag the link/unlink
      // mutations already invalidate.
      providesTags: (_r, _e, { merchantId, vendorIds }) =>
        vendorIds.map((vendorId) => ({
          type: 'Vendor' as const,
          id: `mvp:${merchantId}:${vendorId}`,
        })),
    }),

    /**
     * The routing simulator's SKU catalog: ONE bounded page per vendor,
     * interleaved round-robin across vendors, with the summed totals so the
     * caller can tell a truncated sample from a complete list. Two tiers:
     *
     *  - vendors in `linkedVendorIds` → the merchant-linked overrides,
     *    `GET /v1/merchantVendorProducts?merchantId=&vendorId=&page=1&pageSize=100[&searchAll=]`
     *    (rows flagged `isLinked`) — the set the engine actually routes for
     *    a vendor that has overrides;
     *  - every other vendor → the full default catalog,
     *    `GET /v1/vendorProducts?vendorId=[&searchAll=]&page=1&pageSize=100`
     *    — which is what the engine evaluates for a vendor with none.
     *
     * This deliberately does NOT use `POST /v1/vendorProducts/search` (the
     * single endpoint the engine itself calls): that handler short-circuits
     * to an empty list for business (merchant) callers. Merchant users also
     * cannot read the override layer (403), so for them `linkedVendorIds`
     * stays empty and every vendor takes the full-catalog tier — a display
     * superset; the simulation itself stays server-side and authoritative.
     *
     * Captured (QA, merchant-user token):
     *   GET /vendor/v1/vendorProducts?vendorId={id}&searchAll=Metallic&page=1&pageSize=50
     *   → 200 { vendorProducts: [{ id, sku, variantId, vendorId, vendorName,
     *     productId, facilities: [{ id, vendorSku, vendorSkuDescription,
     *     status, productionTimeDays, productCost, decorations }] }], total }
     */
    getVendorProductsForVendors: builder.query<
      VendorProductsForVendorsResponse,
      VendorProductsForVendorsArgs
    >({
      async queryFn({ vendorIds, searchString, merchantId, linkedVendorIds }, _api, _extra, fetchWithBQ) {
        const linked = new Set(merchantId ? linkedVendorIds ?? [] : []);
        let firstError: FetchBaseQueryError | undefined;
        const failedVendorIds: string[] = [];
        let total = 0;
        const perVendor = await runInChunks(vendorIds, async (vendorId) => {
          const isLinked = linked.has(vendorId);
          const res = await fetchWithBQ({
            url: isLinked
              ? merchantVendorProductsUrl({
                  merchantId: merchantId as string,
                  vendorId,
                  page: 1,
                  pageSize: VENDOR_PRODUCTS_SAMPLE_PAGE_SIZE,
                  searchString,
                })
              : vendorProductsUrl({
                  vendorId,
                  searchString,
                  page: 1,
                  pageSize: VENDOR_PRODUCTS_SAMPLE_PAGE_SIZE,
                }),
            method: 'GET',
          });
          if (res.error) {
            // One vendor down must not blank the other N-1 catalogs — the
            // fan-out makes a single transient failure N× more likely than
            // the old single POST. Survivors ship with the failure surfaced
            // via `failedVendorIds`; only ALL vendors failing is an error.
            firstError ??= res.error;
            failedVendorIds.push(vendorId);
            return [];
          }
          const body = res.data as VendorProductsResponse & MerchantVendorProductsResponse;
          total += body.total ?? 0;
          const rows = (isLinked ? body.merchantVendorProducts : body.vendorProducts) ?? [];
          // Override rows don't carry the vendor inline the way the default-
          // catalog rows do — stamp the vendor we fetched them for.
          return (rows as VendorProductWithVendorRow[]).map((r) => ({
            ...r,
            vendorId: r.vendorId ?? vendorId,
            isLinked,
          }));
        });
        if (firstError && failedVendorIds.length === vendorIds.length) {
          return { error: firstError };
        }
        return {
          data: { vendorProducts: interleaveRoundRobin(perVendor), total, failedVendorIds },
        };
      },
      // Per-vendor, per-tier: a linked-tier sample changes with the merchant's
      // overrides (`mvp:` — what the link/unlink mutations invalidate); a
      // default-tier sample changes with the vendor's own catalog
      // (`products:` — what the facility-product update invalidates).
      providesTags: (_r, _e, { vendorIds, merchantId, linkedVendorIds }) => {
        const linked = new Set(merchantId ? linkedVendorIds ?? [] : []);
        return vendorIds.map((vendorId) =>
          linked.has(vendorId)
            ? { type: 'Vendor' as const, id: `mvp:${merchantId}:${vendorId}` }
            : { type: 'Vendor' as const, id: `products:${vendorId}` },
        );
      },
    }),
  }),
});

export const {
  useGetMerchantLinkedVendorsQuery,
  useGetVendorProductsForVendorsQuery,
} = vendorProductsForVendorsApi;
